import { useQuery } from '@tanstack/react-query';
import { getInstalledModules } from '../../shared/module-flags';

export interface PublicMenuItem {
  title: string;
  path: string;
  enabled?: boolean;
}

export interface PublicConfig {
  modules: Record<string, boolean>;
  business: {
    name: string;
    email: string;
    phone: string;
    address: string;
    hours?: string;
    timezone?: string;
  };
  site?: { name: string; tagline: string; blogName: string; heroHeadline: string };
  menu?: PublicMenuItem[] | null;
  wordpress?: { apiUrl: string | null };
  features?: {
    darkMode: boolean;
    newsletter: boolean;
    socialSharing: boolean;
    comments: boolean;
    search: boolean;
  };
  social?: { platforms: string[] };
  stripePublishableKey: string | null;
}

/** Static-fork fallback: with the API unreachable, every installed module behaves
 *  as enabled and env-derived values render (anchor runtime-with-env-fallback). */
function fallbackConfig(): PublicConfig {
  return {
    modules: { ...getInstalledModules() },
    business: { name: '', email: '', phone: '', address: '' },
    menu: null, // null → getMenuItems falls back to the env menu
    stripePublishableKey: null,
  };
}

export function usePublicConfig() {
  return useQuery<PublicConfig>({
    queryKey: ['config', 'public'],
    staleTime: 60_000,
    retry: 1,
    queryFn: async () => {
      try {
        const res = await fetch('/api/config/public');
        if (!res.ok) return fallbackConfig();
        return (await res.json()) as PublicConfig;
      } catch {
        return fallbackConfig();
      }
    },
  });
}

/** Optimistic: while config loads (or on failure) modules behave as enabled. */
export function useModuleEnabled(id: string): boolean {
  const { data } = usePublicConfig();
  if (id === 'admin') return true; // admin is never runtime-gated
  if (!data) return true;
  return data.modules[id] !== false;
}

/** Feature flag read: undefined config/features → enabled (env fallback rendered
 *  by the consumer; runtime only ever gates VISIBILITY, never build-time CSS). */
export function useFeatureEnabled(feature: keyof NonNullable<PublicConfig['features']>): boolean {
  const { data } = usePublicConfig();
  const value = data?.features?.[feature];
  return value !== false;
}
